import {
  takeLatest,
  take,
  race,
  call,
  put,
  select,
  all,
  fork,
  cancel,
  takeEvery,
} from 'redux-saga/effects'
import { path, head, isNil, isEmpty, prop, propOr } from 'ramda'
import {
  getSearchStringByCollection,
  getFiltersByCollectionForPost,
  getSearchMetaByCollection,
  getResultsLimit,
  getFilterDataByCollection,
  getRPFilterTermByDropdownSelector,
  selectFeatureFilterTermByDropdown,
  selectVehicleFilterTermByDropdown,
  getFiltersByCollectionName,
  getDaysOutByCollection,
  selectChipFeatureFilterTerm,
  getSearchByBusFilterByCollection,
} from 'selectors/search'
import {
  ADVANCED_FILTER_TYPES,
  REFERENCE_PARAMETER,
  SEARCH_PAGE_SIZE,
  SUPPORT_PARAMETER,
  FEATURE,
  filterSchemaForAddRemoveFeatures,
  INTERFACE_CONN_TYPE_FILTER,
  MODULE_TYPE_FILTER,
  FAST_FEATURES,
} from 'utils/constants'
import { toDate } from 'utils/utils'
import { plantLocationsCodes } from 'constants/dash'
import {
  getSelectedLocation,
  getSelectedScheduleDate,
  getProductionScheduleRequestId,
} from 'selectors/dashBuilder'
import * as actions from 'actions/search'
import {
  fetchSearchProductionScheduleResults,
  nextProductionScheduleRequestId,
} from 'actions/dashBuilder'
import { getVehicleCtpDictionary } from 'selectors/ctp'

function* watchAdvSearch({
  collectionName,
  addToSearchRequestBody = {},
  fetchFilters = true,
}) {
  while (true) {
    const { vehicleSubType = '' } = addToSearchRequestBody

    if (fetchFilters && collectionName !== FAST_FEATURES)
      yield put(
        actions.fetchFilterDataByCollection({
          collectionName,
          busFilterFlag: vehicleSubType === 'BUS',
        })
      )

    const backgroundTasks = yield all([
      fork(watchSubmitSearch),
      fork(watchCreateFilterTerm),
      fork(watchRemoveAnyFilters),
    ])

    yield race({
      events: call(watchEvents),
      cancel: take(actions.ADS_END_SEARCH),
    })

    yield cancel(backgroundTasks)
  }
}

export default function* watchSearch() {
  yield takeLatest(actions.ADS_START_SEARCH, watchAdvSearch)
}

export function* watchAdvBusFilterSearch({ collectionName, busFilterFlag }) {
  yield put(
    actions.fetchFilterDataByCollection({ collectionName, busFilterFlag })
  )
  const backgroundTasks = yield all([
    fork(watchSubmitSearch),
    fork(watchCreateFilterTerm),
    fork(watchRemoveAnyFilters),
  ])

  yield cancel(backgroundTasks)
}

export function* watchBusFilterSearch() {
  yield takeEvery(actions.ADS_SET_BUS_FILTER_FLAG, watchAdvBusFilterSearch)
}

function* watchEvents() {
  while (true) {
    yield race({
      [actions.ADS_HANDLE_SUBMIT_SEARCH]: call(watchHandleSubmitSearch),
      [actions.ADS_HANDLE_ADD_FILTER_TERM]: call(watchHandleAddFilterTerm), // working for SP filters
      [actions.ADS_CREATE_ADD_FILTER_TERM]: call(watchCreateAndAddFilterTerm), // working for RP filters TODO: align RP and SP filters
      [actions.ADS_CREATE_ADD_FILTER_TERM_FROM_DASH]: call(
        watchCreateAndAddFilterTermFromDash
      ),
      [actions.ADS_CREATE_ADD_FILTER_TERM_FROM_ECU]: call(
        watchCreateAndAddFilterTermFromEcu
      ),
      [actions.ADS_HANDLE_REMOVE_FILTER_TERM]: call(
        watchHandleRemoveFilterTerm
      ),
      [actions.ADS_HANDLE_PAGINATE_SEARCH]: call(watchHandlePaginateSearch),
      [actions.ADS_HANDLE_CLEAR_SEARCH]: call(watchHandleClearSearch),
      [actions.ADS_HANDLE_CLEAR_ALL_ADVANCED_SEARCH]: call(
        watchHandleClearAllAdvancedSearch
      ),
      [actions.ADS_HANDLE_CLEAR_FILTER_TERMS]: call(
        watchHandleClearFilterTerms
      ),
      [actions.ADS_RESET_SEARCH]: call(watchResetSearch),
      [actions.ADS_CREATE_ADD_FILTER_TERM_DASH_BUILDER_PRODUCTION_SHCEDULE]:
        call(watchCreateAndAddProductionFilterTerm),
      [actions.ADS_HANDLE_CLEAR_PRODUCTION_SCHEDULE_FILTER]: call(
        watchHandleClearProductionScheduleFilters
      ),
      [actions.ADS_HANDLE_PRODUCTION_PAGINATE_SEARCH]: call(
        watchHandleProductionSchedulePaginateSearch
      ),
    })
  }
}

function* watchHandlePaginateSearch() {
  const { page, collectionName, addToSearchRequestBody } = yield take(
    actions.ADS_HANDLE_PAGINATE_SEARCH
  )
  const startIndex = (page - 1) * SEARCH_PAGE_SIZE + 1
  yield put(actions.setCollectionStartIndex(startIndex, collectionName))
  yield call(submitSearch, {
    collectionName,
    resetPagination: false,
    fetchResults: true,
    addToSearchRequestBody,
  })
}

function* watchHandleProductionSchedulePaginateSearch() {
  const { page, collectionName } = yield take(
    actions.ADS_HANDLE_PRODUCTION_PAGINATE_SEARCH
  )
  const startIndex = (page - 1) * SEARCH_PAGE_SIZE + 1
  yield put(actions.setCollectionStartIndex(startIndex, collectionName))
  yield call(submitProductionSearch, { collectionName, resetPagination: false })
}

// SP specific only (will go away with SP filters redo)
function* watchHandleAddFilterTerm() {
  const { primaryFilter, secondaryFilter, groupName, collectionName } =
    yield take(actions.ADS_HANDLE_ADD_FILTER_TERM)
  yield put(
    actions.createFilterTerm(
      primaryFilter,
      secondaryFilter,
      groupName,
      collectionName
    )
  )
}

// RP(NEW): create filter on specific dropdown selection and set in state
function* watchCreateAndAddFilterTerm() {
  const { collectionName, dropdownKey } = yield take(
    actions.ADS_CREATE_ADD_FILTER_TERM
  )
  if (collectionName === REFERENCE_PARAMETER) {
    const filterTerm = yield select(getRPFilterTermByDropdownSelector, {
      dropdownKey,
      collectionName,
    })
    yield put(actions.setFilterTerm(filterTerm, collectionName))
  }
  if (collectionName === FEATURE) {
    const filterTerm = yield select(selectFeatureFilterTermByDropdown, {
      dropdownKey,
      collectionName,
    })
    yield put(actions.setFilterTerm(filterTerm, collectionName))
  }
  yield call(submitSearch, { collectionName, resetPagination: true })
  yield put(actions.clearAllDropdowns(collectionName))
}

// create filter term from S&G
function* watchCreateAndAddFilterTermFromDash() {
  const {
    schema,
    value,
    filterType,
    switches,
    collectionName,
    serialNumber,
    switchName,
  } = yield take(actions.ADS_CREATE_ADD_FILTER_TERM_FROM_DASH)
  if (collectionName === FAST_FEATURES) {
    yield put(
      actions.setFastFeaturesFiltersFromEcuAndDash({
        switchNumber: switchName,
      })
    )
  } else {
    const filterTermData = { schema, value, filterType, switches }

    yield put(actions.setFilterTermFromDash(filterTermData, FEATURE))
  }
  yield call(submitSearch, {
    collectionName,
    resetPagination: true,
    fetchResults: true,
    addToSearchRequestBody: { serial: serialNumber },
  })
}

// create filter term from Ecu
export function* watchCreateAndAddFilterTermFromEcu() {
  const { selectedPin, filterType, serialNumber, collectionName } = yield take(
    actions.ADS_CREATE_ADD_FILTER_TERM_FROM_ECU
  )
  if (filterType === INTERFACE_CONN_TYPE_FILTER) {
    const { ecuPinUri, description, currentLimit } = selectedPin
    if (collectionName === FAST_FEATURES) {
      yield put(
        actions.setFastFeaturesFiltersFromEcuAndDash({
          current: `${parseInt(currentLimit)}A`,
        })
      )
    } else {
      const [, module, connector, pin] = ecuPinUri.split('/')
      const label = `${module}-${connector}-${pin}`
      const ctpDictionary = yield select(getVehicleCtpDictionary, {
        serialNumber,
      })
      const { pinNumber = null } = ctpDictionary[ecuPinUri] || {}
      const filterTermData = {
        schema: filterSchemaForAddRemoveFeatures[filterType],
        value: pinNumber ? `${module}|${pinNumber}` : null,
        filterType,
        pin: label,
        description,
      }
      yield put(actions.setFilterTermFromEcu(filterTermData, FEATURE))
    }
  }
  if (filterType === MODULE_TYPE_FILTER) {
    const { uri, description, pinNumber, currentLimit } = selectedPin
    if (collectionName === FAST_FEATURES) {
      yield put(
        actions.setFastFeaturesFiltersFromEcuAndDash({
          current: `${parseInt(currentLimit)}A`,
        })
      )
    } else {
      const [, moduleType, connector, pin] = uri.split('/')
      const pinType = description.split(' ')[1].toLowerCase()

      const filter1 = {
        schema: filterSchemaForAddRemoveFeatures[filterType],
        value: moduleType,
        filterType,
        moduleType,
        description,
      }
      const filter2 = {
        schema: filterSchemaForAddRemoveFeatures[pinType],
        value: pinNumber,
        label: `${connector}-${pin}`,
        pin: `${connector}-${pin}`,
        description,
        filterType,
      }
      const filterKey = `${pinNumber}--${filterType}_${moduleType}_${pinType}--${filterSchemaForAddRemoveFeatures[pinType]}`
      const filterTermData = {
        filter1: { ...filter1 },
        filter2: { ...filter2 },
        value: filterKey,
        filterType,
      }
      yield put(actions.setFilterTermFromEcu(filterTermData, FEATURE))
    }
  }

  yield call(submitSearch, {
    collectionName,
    resetPagination: true,
    fetchResults: true,
    addToSearchRequestBody: { serial: serialNumber },
  })
}

function* watchCreateAndAddProductionFilterTerm() {
  const { collectionName } = yield take(
    actions.ADS_CREATE_ADD_FILTER_TERM_DASH_BUILDER_PRODUCTION_SHCEDULE
  )
  const filterTerm = yield select(selectVehicleFilterTermByDropdown, {
    collectionName,
  })
  yield put(actions.setFilterTerms(filterTerm, collectionName))
  yield call(submitProductionSearch, { collectionName, resetPagination: true })
}

function* watchHandleRemoveFilterTerm() {
  const { value, collectionName } = yield take(
    actions.ADS_HANDLE_REMOVE_FILTER_TERM
  )
  yield put(actions.removeFilterTerm(value, collectionName))
}

function* watchHandleClearFilterTerms() {
  const { collectionName } = yield take(actions.ADS_HANDLE_CLEAR_FILTER_TERMS)
  yield put(actions.clearFilterTerms(collectionName))
  yield put(actions.clearAllDropdowns(collectionName))
}

function* watchHandleClearSearch() {
  const { collectionName } = yield take(actions.ADS_HANDLE_CLEAR_SEARCH)
  yield put(actions.clearSearchString(collectionName))
}

function* watchHandleClearAllAdvancedSearch() {
  const { collectionName } = yield take(
    actions.ADS_HANDLE_CLEAR_ALL_ADVANCED_SEARCH
  )
  yield put(actions.clearSearchString(collectionName))
  yield put(actions.clearFilterTerms(collectionName))
  yield put(actions.clearAllDropdowns(collectionName))
  yield put(actions.setIsSearching(false))
}

// SP specific only (will go away with SP filters redo)
function* watchCreateFilterTerm() {
  yield takeLatest(actions.ADS_CREATE_FILTER_TERM, createFilterTerm)
}
// SP specific only (will go away with SP filters redo)
function* createFilterTerm(action) {
  if (isNil(action)) return
  try {
    const { collectionName } = action
    switch (collectionName) {
      case SUPPORT_PARAMETER:
        yield call(addFilterTermSupportParameter, action)
        break
      default:
        break
    }
    yield call(submitSearch, { collectionName, resetPagination: true }) // this resets pagination
  } catch (e) {
    console.error(e)
  }
}

// SP specific only (will go away with SP filters redo)
function* addFilterTermSupportParameter(action) {
  try {
    const { collectionName, secondaryFilter, groupName } = action
    const filterGroups = yield select(getFilterDataByCollection, collectionName)
    const pathToValue = [groupName, 'groupFilter', 'schemaId']
    const allFilters = {}
    allFilters[ADVANCED_FILTER_TYPES[groupName]] = {
      schema: path([...pathToValue], filterGroups),
      groupName,
      value: ADVANCED_FILTER_TYPES[groupName],
      type: ADVANCED_FILTER_TYPES[groupName],
    }
    if (secondaryFilter) {
      const secondarySchema = prop(
        'schema',
        head(
          Object.values(filterGroups[groupName].filters).filter(
            (value) => value.value === secondaryFilter
          )
        )
      )
      allFilters[secondaryFilter] = {
        schema: secondarySchema,
        groupName,
        value: { type: secondaryFilter },
        type: ADVANCED_FILTER_TYPES[groupName],
      }
    }
    yield put(actions.setFilterTerms(allFilters, collectionName))
  } catch (e) {
    console.error(e)
  }
}

function* watchRemoveAnyFilters() {
  yield takeLatest(
    [
      actions.ADS_REMOVE_FILTER_TERM,
      actions.ADS_CLEAR_FILTER_TERMS,
      actions.ADS_CLEAR_SEARCH_STRING,
    ],
    removeOrClearAnyFilterTerms
  )
}

function* removeOrClearAnyFilterTerms(action) {
  if (isNil(action)) return
  try {
    const { collectionName } = action
    yield call(submitSearch, { collectionName, resetPagination: true })
  } catch (e) {
    console.error(e)
  }
}

function* watchHandleSubmitSearch() {
  const { collectionName, resetPagination, addToSearchRequestBody } =
    yield take(actions.ADS_HANDLE_SUBMIT_SEARCH)
  yield put(actions.submitSearchStart(true, collectionName))
  yield put(
    actions.submitSearch(
      collectionName,
      resetPagination,
      addToSearchRequestBody
    )
  )
}

function* watchSubmitSearch() {
  yield takeLatest(actions.ADS_SUBMIT_SEARCH, submitSearch)
}

function* submitSearch(action) {
  if (isNil(action)) return
  try {
    const {
      collectionName,
      resetPagination,
      fetchResults = true,
      addToSearchRequestBody,
    } = action
    const [
      filters,
      searchString,
      {
        searchUri,
        addToSearchRequestBody: additionalRequestBodyParams,
        startIndex,
      },
      limit,
      chipFilters,
      busFilterFlag,
    ] = yield all([
      select(getFiltersByCollectionForPost, collectionName),
      select(getSearchStringByCollection, collectionName),
      select(getSearchMetaByCollection, collectionName),
      select(getResultsLimit),
      select(selectChipFeatureFilterTerm, collectionName), // only applies for feature search
      select(getSearchByBusFilterByCollection, collectionName),
    ])
    const isFastFeaturesSearch = collectionName === FAST_FEATURES

    if (isEmpty(filters) && isEmpty(searchString)) {
      yield put(
        actions.resetCollectionSearchResultsAndInitialState(collectionName)
      )
    } else {
      if (resetPagination) {
        yield put(actions.setCollectionStartIndex(1, collectionName))
      }
      yield put(actions.setIsSearching(true))
      if (fetchResults) {
        let requestBody = {}
        if (isFastFeaturesSearch) {
          const ecuTypes = chipFilters?.queries?.ecuTypes || ['XMC 3', 'XMC 4']
          requestBody = {
            searchDescription: searchString,
            current: filters.current,
            switchName: filters.switchNumber,
            solenoid: filters.solenoid,
            serial: addToSearchRequestBody?.serial,
            ecuTypes,
          }
        } else {
          const queries = propOr(null, 'queries')(chipFilters) || {}

          requestBody = {
            filters,
            search: searchString,
            vehicleSubType: busFilterFlag ? 'BUS' : '',
            ...queries,
            ...addToSearchRequestBody,
            ...additionalRequestBodyParams,
          }
        }

        yield put(
          actions.fetchSearchResults({
            uri: searchUri,
            collectionName,
            requestBody: {
              ...requestBody,
              count: limit,
              startIndex: resetPagination ? 1 : startIndex,
              ...requestBody,
            },
          })
        )
      }
    }
  } catch (e) {
    console.error(e)
  }
}

function* submitProductionSearch(action) {
  if (isNil(action)) return
  try {
    const { collectionName, resetPagination } = action
    const [
      filters,
      searchString,
      date,
      location,
      selectedDaysOut,
      { addToSearchRequestBody, startIndex },
      limit,
    ] = yield all([
      select(getFiltersByCollectionName, collectionName),
      select(getSearchStringByCollection, collectionName),
      select(getSelectedScheduleDate),
      select(getSelectedLocation),
      select(getDaysOutByCollection, collectionName),
      select(getSearchMetaByCollection, collectionName),
      select(getResultsLimit),
    ])
    const searchUri = 'vehicles/production-schedule'
    const requestBody = {
      filters,
      daysOut: Number(selectedDaysOut),
      location: plantLocationsCodes[location],
      date: toDate(date),
      search: searchString,
      count: limit,
      startIndex: resetPagination ? 1 : startIndex,
      ...addToSearchRequestBody,
    }
    if (resetPagination) {
      yield put(actions.setCollectionStartIndex(1, collectionName))
    }
    yield put(actions.setIsSearching(true))
    // Stamp this request with a fresh identity so its response can be matched
    // against what the user is looking at when it lands, and so apiCall's
    // in-flight de-dupe doesn't drop it as a duplicate of a different query.
    yield put(nextProductionScheduleRequestId())
    const requestId = yield select(getProductionScheduleRequestId)
    yield put(
      fetchSearchProductionScheduleResults({
        searchUri,
        collectionName,
        requestBody,
        requestId,
      })
    )
    yield put(actions.setIsSearching(false))
  } catch (e) {
    console.error(e)
  }
}

function* watchResetSearch() {
  yield take(actions.ADS_RESET_SEARCH)

  yield put(actions.resetAllSearch())
}

function* watchHandleClearProductionScheduleFilters() {
  const { collectionName } = yield take(
    actions.ADS_HANDLE_CLEAR_PRODUCTION_SCHEDULE_FILTER
  )
  yield put(actions.clearFilterTerms(collectionName))
  yield put(actions.clearAllDropdowns(collectionName))
  yield put(actions.clearSearchString(collectionName))
  yield put(actions.resetSelectedDaysOut(collectionName))
}
